import * as z from 'zod';

export const CreateRefundRequest = z.object({
  refund: z
    .object({
      _id: z
        .string()
        .describe('Refund ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the refund is updated.\n\nIgnored when creating a refund.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the refund was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the refund object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      chargeId: z
        .string()
        .describe('ID of the charge to refund.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      currencyCode: z
        .string()
        .describe('Refund currency. Should be the same as the charge currency.')
        .optional()
        .nullable(),
      amount: z
        .string()
        .describe(
          'Amount being returned to the buyer in the refund currency\'s main units (such as dollars or euros).\nFor example, `"12.95"`.'
        )
        .optional()
        .nullable(),
      returnedApplicationFee: z
        .string()
        .describe(
          'Application fee returned to the merchant from Wix in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nOnly included if an application fee was returned.'
        )
        .optional()
        .nullable(),
      returnedProcessingFee: z
        .string()
        .describe(
          'Processing fee returned to the merchant from the payment service provider in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nThis field is empty when the processing fee is unknown.'
        )
        .optional()
        .nullable(),
      full: z
        .boolean()
        .describe('Whether the refund is for the entire amount of the charge.')
        .optional()
        .nullable(),
      status: z.enum(['PENDING', 'SUCCEEDED', 'FAILED', 'REVERSED']).optional(),
      providerRefundId: z
        .string()
        .describe("Payment service provider's ID for the refund.")
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      reason: z
        .string()
        .describe('Reason this refund was issued.')
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      statusInfo: z
        .object({
          code: z
            .string()
            .describe(
              'Reason code with detailed information about the refund status.\nSee the full list of [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes#refund-declined).'
            )
            .min(1)
            .max(10)
            .optional(),
          description: z
            .string()
            .describe('Description of the status.')
            .min(1)
            .max(1000)
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the refund status.\nUsed to provide additional information about why a refund was given its status.'
        )
        .optional(),
      acquirerReferenceNumber: z
        .string()
        .describe(
          'Unique number assigned to a refund. This number is shared across all parties involved in processing the refund. It allows the merchant to track the refund with their bank.'
        )
        .min(1)
        .max(30)
        .optional()
        .nullable(),
      note: z
        .string()
        .describe('Note providing additional information about this refund.')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Refund to be created.'),
  options: z
    .object({
      previouslyRefundedAmount: z
        .string()
        .describe(
          "Amount of the charge that was previously refunded.\n\nSpecify this to compare your records of previously refunded amounts against those on the server. If they don't match, this method will return an error with the code `PREVIOUSLY_REFUNDED_AMOUNT_MISMATCH`.\n\nLearn more about [preventing unintended refunds](https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#preventing-unintended-refunds)."
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CreateRefundResponse = z.object({
  _id: z
    .string()
    .describe('Refund ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the refund is updated.\n\nIgnored when creating a refund.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the refund was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the refund object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  chargeId: z
    .string()
    .describe('ID of the charge to refund.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  currencyCode: z
    .string()
    .describe('Refund currency. Should be the same as the charge currency.')
    .optional()
    .nullable(),
  amount: z
    .string()
    .describe(
      'Amount being returned to the buyer in the refund currency\'s main units (such as dollars or euros).\nFor example, `"12.95"`.'
    )
    .optional()
    .nullable(),
  returnedApplicationFee: z
    .string()
    .describe(
      'Application fee returned to the merchant from Wix in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nOnly included if an application fee was returned.'
    )
    .optional()
    .nullable(),
  returnedProcessingFee: z
    .string()
    .describe(
      'Processing fee returned to the merchant from the payment service provider in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nThis field is empty when the processing fee is unknown.'
    )
    .optional()
    .nullable(),
  full: z
    .boolean()
    .describe('Whether the refund is for the entire amount of the charge.')
    .optional()
    .nullable(),
  status: z
    .enum(['PENDING', 'SUCCEEDED', 'FAILED', 'REVERSED'])
    .describe(
      'Status of the refund.\n\nLearn more about the [refund lifecycle](https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#lifecycle-of-a-refund>).'
    )
    .optional(),
  providerRefundId: z
    .string()
    .describe("Payment service provider's ID for the refund.")
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  reason: z
    .string()
    .describe('Reason this refund was issued.')
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
  statusInfo: z
    .object({
      code: z
        .string()
        .describe(
          'Reason code with detailed information about the refund status.\nSee the full list of [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes#refund-declined).'
        )
        .min(1)
        .max(10)
        .optional(),
      description: z
        .string()
        .describe('Description of the status.')
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
    })
    .describe(
      'Details about the refund status.\nUsed to provide additional information about why a refund was given its status.'
    )
    .optional(),
  acquirerReferenceNumber: z
    .string()
    .describe(
      'Unique number assigned to a refund. This number is shared across all parties involved in processing the refund. It allows the merchant to track the refund with their bank.'
    )
    .min(1)
    .max(30)
    .optional()
    .nullable(),
  note: z
    .string()
    .describe('Note providing additional information about this refund.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
});
export const GetRefundRequest = z.object({
  refundId: z
    .string()
    .describe('ID of the refund to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetRefundResponse = z.object({
  _id: z
    .string()
    .describe('Refund ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the refund is updated.\n\nIgnored when creating a refund.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the refund was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the refund object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  chargeId: z
    .string()
    .describe('ID of the charge to refund.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  currencyCode: z
    .string()
    .describe('Refund currency. Should be the same as the charge currency.')
    .optional()
    .nullable(),
  amount: z
    .string()
    .describe(
      'Amount being returned to the buyer in the refund currency\'s main units (such as dollars or euros).\nFor example, `"12.95"`.'
    )
    .optional()
    .nullable(),
  returnedApplicationFee: z
    .string()
    .describe(
      'Application fee returned to the merchant from Wix in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nOnly included if an application fee was returned.'
    )
    .optional()
    .nullable(),
  returnedProcessingFee: z
    .string()
    .describe(
      'Processing fee returned to the merchant from the payment service provider in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nThis field is empty when the processing fee is unknown.'
    )
    .optional()
    .nullable(),
  full: z
    .boolean()
    .describe('Whether the refund is for the entire amount of the charge.')
    .optional()
    .nullable(),
  status: z
    .enum(['PENDING', 'SUCCEEDED', 'FAILED', 'REVERSED'])
    .describe(
      'Status of the refund.\n\nLearn more about the [refund lifecycle](https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#lifecycle-of-a-refund>).'
    )
    .optional(),
  providerRefundId: z
    .string()
    .describe("Payment service provider's ID for the refund.")
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  reason: z
    .string()
    .describe('Reason this refund was issued.')
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
  statusInfo: z
    .object({
      code: z
        .string()
        .describe(
          'Reason code with detailed information about the refund status.\nSee the full list of [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes#refund-declined).'
        )
        .min(1)
        .max(10)
        .optional(),
      description: z
        .string()
        .describe('Description of the status.')
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
    })
    .describe(
      'Details about the refund status.\nUsed to provide additional information about why a refund was given its status.'
    )
    .optional(),
  acquirerReferenceNumber: z
    .string()
    .describe(
      'Unique number assigned to a refund. This number is shared across all parties involved in processing the refund. It allows the merchant to track the refund with their bank.'
    )
    .min(1)
    .max(30)
    .optional()
    .nullable(),
  note: z
    .string()
    .describe('Note providing additional information about this refund.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
});
export const QueryRefundsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          chargeId: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          _createdDate: z.object({}).partial().strict().optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['_createdDate']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryRefundsResponse = z.object({
  refunds: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Refund ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the refund is updated.\n\nIgnored when creating a refund.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the refund was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format. For example, "2024-01-30T13:30:00".'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the refund object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        chargeId: z
          .string()
          .describe('ID of the charge to refund.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        currencyCode: z
          .string()
          .describe(
            'Refund currency. Should be the same as the charge currency.'
          )
          .optional()
          .nullable(),
        amount: z
          .string()
          .describe(
            'Amount being returned to the buyer in the refund currency\'s main units (such as dollars or euros).\nFor example, `"12.95"`.'
          )
          .optional()
          .nullable(),
        returnedApplicationFee: z
          .string()
          .describe(
            'Application fee returned to the merchant from Wix in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nOnly included if an application fee was returned.'
          )
          .optional()
          .nullable(),
        returnedProcessingFee: z
          .string()
          .describe(
            'Processing fee returned to the merchant from the payment service provider in the refund currency\'s main units (such as dollars or euros). For example, `"12.95"`.\n\nThis field is empty when the processing fee is unknown.'
          )
          .optional()
          .nullable(),
        full: z
          .boolean()
          .describe(
            'Whether the refund is for the entire amount of the charge.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['PENDING', 'SUCCEEDED', 'FAILED', 'REVERSED'])
          .describe(
            'Status of the refund.\n\nLearn more about the [refund lifecycle](https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#lifecycle-of-a-refund>).'
          )
          .optional(),
        providerRefundId: z
          .string()
          .describe("Payment service provider's ID for the refund.")
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        reason: z
          .string()
          .describe('Reason this refund was issued.')
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        statusInfo: z
          .object({
            code: z
              .string()
              .describe(
                'Reason code with detailed information about the refund status.\nSee the full list of [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes#refund-declined).'
              )
              .min(1)
              .max(10)
              .optional(),
            description: z
              .string()
              .describe('Description of the status.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the refund status.\nUsed to provide additional information about why a refund was given its status.'
          )
          .optional(),
        acquirerReferenceNumber: z
          .string()
          .describe(
            'Unique number assigned to a refund. This number is shared across all parties involved in processing the refund. It allows the merchant to track the refund with their bank.'
          )
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        note: z
          .string()
          .describe('Note providing additional information about this refund.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
